import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { RecordsTable } from "@/components/RecordsTable";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { EVENT_LABELS } from "@/lib/constants";
import { formatDate } from "@/lib/format";
import { TrendingUp, AlertTriangle, Wallet, CalendarCheck, Plus, Inbox, Activity, Lightbulb, ArrowRight } from "lucide-react";

function Metric({ label, value, sub, Icon, tone = "default", testid }) {
  const dot = { default: "bg-brandblue", overdue: "bg-status-danger", success: "bg-status-success" }[tone];
  const iconWrap = {
    default: "bg-brandblue-light text-brandblue", overdue: "bg-[#FDECEC] text-status-danger",
    success: "bg-[#E7F6EF] text-status-success",
  }[tone];
  return (
    <div className="border rounded-2xl bg-white p-5 transition-transform duration-200 hover:-translate-y-0.5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold tracking-[0.12em] uppercase text-ink-muted">{label}</span>
        <span className={`grid place-items-center w-8 h-8 rounded-lg ${iconWrap}`}><Icon size={16} /></span>
      </div>
      <p className="font-num font-bold text-3xl mt-4 text-midnight tracking-tight" data-testid={`${testid}-value`}>{value}</p>
      <p className="text-xs text-ink-secondary mt-1.5 flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} /> {sub}
      </p>
    </div>
  );
}

export default function Overview() {
  const { openCreate, triggerRefresh, refreshKey } = useOutletContext();
  const [metrics, setMetrics] = useState(null);
  const [records, setRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [m, inv, pay, ev] = await Promise.all([
        api.get("/dashboard/metrics"), api.get("/invoices"), api.get("/payouts"),
        api.get("/events?limit=8"),
      ]);
      setMetrics(m.data);
      const all = [...inv.data, ...pay.data].sort((a, b) => (a.date < b.date ? 1 : -1));
      setRecords(all);
      setEvents(ev.data.filter((e) => e.entity_type !== "account").slice(0, 8));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doAction = async (record, action) => {
    try {
      const base = record.type === "invoice" ? "invoices" : "payouts";
      await api.post(`/${base}/${record.id}/${action}`);
      toast.success("Record updated");
      load(); triggerRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded-lg p-6" data-testid="overview-error">
      {error} <Button variant="outline" className="ml-3" onClick={load}>Retry</Button>
    </div>;
  }

  const empty = records.length === 0;

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Overview</h1>
        <p className="text-espresso-soft mt-1">Here's what money is coming — and what hasn't arrived.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Expected" value={metrics.expected} sub="Unpaid invoices + pending payouts" Icon={TrendingUp} testid="metric-expected" />
        <Metric label="Overdue" value={metrics.overdue} sub="Past due, not paid" Icon={AlertTriangle} tone="overdue" testid="metric-overdue" />
        <Metric label="Received this month" value={metrics.received_month} sub="Marked paid this month" Icon={Wallet} tone="success" testid="metric-received-month" />
        <Metric label="Paid YTD" value={metrics.paid_ytd} sub="This calendar year" Icon={CalendarCheck} testid="metric-paid-ytd" />
      </div>

      {metrics.overdue_cents > 0 && (
        <Link to="/blog/chasing-late-invoices-without-burning-bridges" data-testid="inapp-tip"
          className="flex items-center gap-3 rounded-2xl border border-lavender bg-lavender/40 p-4 hover:bg-lavender/60 transition-colors">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-white text-brandblue shrink-0"><Lightbulb size={18} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-midnight">You have overdue invoices — here's how to chase them</p>
            <p className="text-xs text-ink-secondary truncate">Read: Chasing Late Invoices Without Burning the Relationship</p>
          </div>
          <ArrowRight size={18} className="text-brandblue shrink-0" />
        </Link>
      )}

      {empty ? (
        <div className="border rounded-2xl bg-white p-12 text-center" data-testid="overview-empty">
          <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-brandblue-light mx-auto mb-4"><Inbox size={26} className="text-brandblue" /></span>
          <h3 className="font-heading text-xl font-bold text-midnight">Nothing to chase yet.</h3>
          <p className="text-ink-secondary mt-1 max-w-sm mx-auto">Add your first invoice or expected payout to start tracking.</p>
          <Button className="mt-5" onClick={() => openCreate(null)} data-testid="empty-add-record"><Plus size={16} className="mr-1" /> Add first record</Button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold">Recent records</h2>
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/ledger")} data-testid="view-all-ledger">View all</Button>
          </div>
          <RecordsTable records={records.slice(0, 6)} onAction={doAction} onEdit={openCreate} onRefresh={() => { load(); triggerRefresh(); }} />
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={18} className="text-brandblue" />
              <h2 className="font-heading text-lg font-bold">Recent activity</h2>
            </div>
            {events.length === 0 ? (
              <div className="border rounded-2xl bg-white p-6 text-sm text-ink-muted" data-testid="activity-empty">No activity yet.</div>
            ) : (
              <div className="border rounded-2xl bg-white divide-y" data-testid="recent-activity">
                {events.map((e) => (
                  <div key={e.id} className="flex items-center justify-between px-5 py-3" data-testid={`activity-item-${e.event_type}`}>
                    <span className="text-sm text-midnight font-medium">{EVENT_LABELS[e.event_type] || e.event_type}</span>
                    <span className="text-xs text-ink-muted">{formatDate(e.event_timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
